import { useEffect, useReducer, useRef, useState } from 'react';
import { AlertCircle, ChevronLeft, ClipboardList, Mail, User } from 'lucide-react';
import { textIn, type AnswerError, type Answers } from '@rx-manager/forms';
import type { DraftAnswers } from '@rx-manager/forms/react';
import {
  zonedToday,
  type CheckInArrival,
  type CheckInWalkInRequest,
  type PublicBookingForm,
  type PublicCheckInPlace,
} from '@rx-manager/shared';
import { BookingApiError, checkInApi } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { dobProblem, isEmail, isUsablePhone } from '../../lib/validate';
import { Alert, Button, CHECKBOX, Field, HEADING, IconTile, Input, StepActions } from '../../components/ui';
import { FormsSummary } from '../booking/FormsSummary';
import { QuestionsStep } from '../booking/QuestionsStep';
import {
  NO_FORMS,
  allSettled,
  confirmForms,
  firstUnsettled,
  formLang,
  formsReducer,
  hasForms,
  type BookingFormsState,
} from '../booking/booking-forms';
import { TextWhenCalledBox, checkInFailure, failureText, tile, type Failure } from './shared';

/**
 * Track CIQ (CIQ-D8, D9) — joining the line as a walk-in, from a place's check-in page while it
 * takes walk-ins: the service (a choice only when there is more than one), who they are, a mobile
 * or an email, "Text me when it's my turn" where the pharmacy can text, and the service's forms,
 * asked by the booking's own runner one question at a time. Required forms are answered before
 * Join the line; the answers go with the join, in memory until then (SFM-D35).
 */

type Person = { firstName: string; lastName: string; birthDate: string; phone: string; email: string };
type ErrorKey = 'service' | keyof Person | 'contact';
type Errors = Partial<Record<ErrorKey, MessageKey>>;

/** Where each problem is fixed, in the order the page asks. */
const FIELD_ID: Record<Exclude<ErrorKey, 'service'>, string> = {
  firstName: 'walk-in-first',
  lastName: 'walk-in-last',
  birthDate: 'walk-in-dob',
  phone: 'walk-in-phone',
  email: 'walk-in-email',
  contact: 'walk-in-phone',
};
const ORDER: ErrorKey[] = ['service', 'firstName', 'lastName', 'birthDate', 'phone', 'email', 'contact'];

/** A service's forms, held as the booking's runner holds them. */
function formsFor(service: { id: string; forms: PublicBookingForm[] } | undefined): BookingFormsState {
  if (!service) return NO_FORMS;
  return formsReducer(formsReducer(NO_FORMS, { type: 'load_start', serviceId: service.id }), {
    type: 'loaded',
    serviceId: service.id,
    forms: service.forms,
  });
}

const SECTION_TITLE = 'flex items-center gap-2.5 text-lg font-semibold text-heading';

export function WalkInJoin({
  code,
  place,
  kiosk,
  prefill,
  onBack,
  onJoined,
  onStartOver,
  onReload,
}: {
  code: string;
  place: PublicCheckInPlace;
  kiosk: boolean;
  /** What Find my visit was given, so nobody types it twice. */
  prefill: { lastName: string; birthDate: string };
  onBack: () => void;
  onJoined: (arrival: CheckInArrival) => void;
  /** "Unable to continue" from a form: back to the start. */
  onStartOver: () => void;
  /** The place again, for a service whose forms changed since the page opened. */
  onReload: () => Promise<PublicCheckInPlace | null>;
}) {
  const { t, locale } = useI18n();
  const services = place.walkIns.services;
  const only = services.length === 1 ? services[0] : undefined;
  const [serviceId, setServiceId] = useState(only?.id ?? '');
  const [forms, dispatch] = useReducer(formsReducer, only, formsFor);
  const [stage, setStage] = useState<'details' | 'questions'>('details');
  const [person, setPerson] = useState<Person>({
    firstName: '',
    lastName: prefill.lastName,
    birthDate: prefill.birthDate,
    phone: '',
    email: '',
  });
  const [texts, setTexts] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** Gone (Start over, the kiosk's timers): a late answer lands nowhere. */
  const gone = useRef(false);

  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
    };
  }, []);

  useEffect(() => {
    headingRef.current?.focus();
  }, [stage]);

  const service = services.find((entry) => entry.id === serviceId);
  const phone = person.phone.trim();
  /** "Text me" is about the mobile given here, so it is offered only once there is one. */
  const offerText = place.textWhenCalled && phone !== '';
  const formsNeeded = hasForms(forms);
  const formsReady = !formsNeeded || allSettled(forms);

  const choose = (id: string): void => {
    const next = services.find((entry) => entry.id === id);
    if (!next) return;
    setServiceId(id);
    setErrors((current) => ({ ...current, service: undefined }));
    // Another service: its own forms, and nothing of the last one's answers.
    if (forms.serviceId !== id) {
      dispatch({ type: 'load_start', serviceId: id });
      dispatch({ type: 'loaded', serviceId: id, forms: next.forms });
    }
  };

  const openForm = (index: number): void => {
    setFailure(null);
    dispatch({ type: 'open', index });
    setStage('questions');
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!service) next.service = 'checkIn.serviceRequired';
    if (!person.firstName.trim()) next.firstName = 'error.fieldRequired';
    if (!person.lastName.trim()) next.lastName = 'error.fieldRequired';
    const dob = dobProblem(person.birthDate);
    if (dob) next.birthDate = dob;
    const email = person.email.trim();
    if (phone && !isUsablePhone(phone)) next.phone = 'error.phoneInvalid';
    if (email && !isEmail(email)) next.email = 'error.emailInvalid';
    if (!phone && !email) next.contact = 'waitlist.contactRequired';
    return next;
  };

  const join = async (): Promise<void> => {
    const problems = validate();
    setErrors(problems);
    const first = ORDER.find((key) => problems[key]);
    if (first) {
      const id = first === 'service' ? `walk-in-service-${services[0]?.id ?? ''}` : FIELD_ID[first];
      document.getElementById(id)?.focus();
      return;
    }
    if (!service) return;
    if (!formsReady) {
      openForm(Math.max(firstUnsettled(forms), 0));
      return;
    }
    const email = person.email.trim();
    const answers = confirmForms(forms, locale);
    const body: CheckInWalkInRequest = {
      serviceId: service.id,
      firstName: person.firstName.trim(),
      lastName: person.lastName.trim(),
      birthDate: person.birthDate,
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
      textWhenCalled: offerText && texts,
      ...(answers ? { forms: answers } : {}),
      via: kiosk ? 'kiosk' : 'poster',
      language: locale,
    };
    setBusy(true);
    setFailure(null);
    try {
      const arrival = await checkInApi.joinWalkIn(code, body);
      if (!gone.current) onJoined(arrival);
    } catch (error) {
      if (gone.current) return;
      if (error instanceof BookingApiError && error.code === 'phone_invalid') {
        setErrors({ phone: 'checkIn.phoneInvalid' });
        document.getElementById(FIELD_ID.phone)?.focus();
        return;
      }
      if (error instanceof BookingApiError && (await formRefusal(service.id, error))) return;
      setFailure(checkInFailure(error));
    } finally {
      if (!gone.current) setBusy(false);
    }
  };

  /** The service's forms again, from the place: they changed since the page opened. */
  const formsAgain = async (id: string): Promise<PublicBookingForm[] | null> => {
    const fresh = await onReload();
    const list = fresh?.walkIns.services.find((entry) => entry.id === id)?.forms ?? null;
    if (list && !gone.current) dispatch({ type: 'loaded', serviceId: id, forms: list });
    return list;
  };

  /** The server refused the forms and nobody joined (SFM-D31): back to the form it names. */
  const formRefusal = async (id: string, error: BookingApiError): Promise<boolean> => {
    const formId = typeof error.extra.formId === 'string' ? error.extra.formId : null;
    const index = formId ? forms.entries.findIndex((entry) => entry.form.formId === formId) : -1;
    switch (error.code) {
      case 'form_answers_required': {
        const list = await formsAgain(id);
        const missing: unknown[] = Array.isArray(error.extra.formIds) ? error.extra.formIds : [];
        const at = (list ?? []).findIndex((form) => missing.includes(form.formId));
        if (at >= 0) openForm(at);
        setFailure({ key: 'checkIn.formsChanged' });
        return true;
      }
      case 'invalid_answers': {
        const answerErrors = (error.extra.errors ?? {}) as Record<string, AnswerError>;
        if (index < 0 || Object.keys(answerErrors).every((key) => key === '_form')) {
          await formsAgain(id);
          setFailure({ key: 'checkIn.formsChanged' });
          return true;
        }
        dispatch({ type: 'open', index, view: { kind: 'runner', mount: 0, startAtReview: true, errors: answerErrors } });
        setStage('questions');
        return true;
      }
      case 'form_stopped': {
        if (index < 0) return false;
        const questionId = typeof error.extra.questionId === 'string' ? error.extra.questionId : undefined;
        dispatch({ type: 'open', index, view: { kind: 'runner', mount: 0, startAt: questionId } });
        setStage('questions');
        return true;
      }
      case 'forms_unavailable':
        // Join the line again goes ahead without them; the pharmacy asks at the counter.
        dispatch({ type: 'unavailable' });
        setFailure({ key: 'checkIn.formsUnavailable' });
        return true;
      default:
        return false;
    }
  };

  /** After a form: the next one still to answer, or back to Join the line. */
  const afterForm = (after: BookingFormsState): void => {
    const next = firstUnsettled(after);
    if (next >= 0) {
      dispatch({ type: 'open', index: next });
      return;
    }
    setStage('details');
  };

  if (stage === 'questions') {
    const entry = forms.entries[forms.cursor];
    return (
      <div className="space-y-5">
        {entry && (
          <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
            {textIn(entry.form.schema.title, formLang(entry.form, locale))}
          </h1>
        )}
        {failure && <Alert>{failureText(failure, t)}</Alert>}
        <QuestionsStep
          state={forms}
          pageLang={locale}
          today={zonedToday(place.place.timezone)}
          phone={kiosk ? null : place.brand.phone}
          optionalIntro={t('checkIn.formOptionalIntro')}
          onAnswers={(answers: DraftAnswers) => dispatch({ type: 'answers', answers })}
          onFinish={(checked: Answers) => {
            const after = formsReducer(forms, { type: 'finish', checked });
            dispatch({ type: 'finish', checked });
            afterForm(after);
            return Promise.resolve();
          }}
          onSkip={() => {
            const after = formsReducer(forms, { type: 'skip' });
            dispatch({ type: 'skip' });
            afterForm(after);
          }}
          onAnswerNow={() => dispatch({ type: 'open', index: forms.cursor, view: { kind: 'runner', mount: 0 } })}
          onBack={() => {
            setFailure(null);
            const current = forms.entries[forms.cursor];
            if (forms.view.kind === 'runner' && current && !current.form.required) {
              dispatch({ type: 'open', index: forms.cursor, view: { kind: 'intro' } });
            } else if (forms.cursor > 0) {
              dispatch({ type: 'open', index: forms.cursor - 1 });
            } else {
              setStage('details');
            }
          }}
          onLeave={onStartOver}
          leaveLabel={t('checkIn.startOver')}
        />
      </div>
    );
  }

  const field = (key: keyof Person, label: MessageKey, autoComplete: string, type = 'text', required = true) => {
    const error = errors[key];
    return (
      <Field id={FIELD_ID[key]} label={t(label)} error={error ? t(error) : undefined} required={required}>
        {(props) => (
          <Input
            {...props}
            type={type}
            // A counter's tablet keeps nobody's name for the next person to be offered.
            autoComplete={kiosk ? 'off' : autoComplete}
            value={person[key]}
            onChange={(event) => {
              const value = event.target.value;
              setPerson((current) => ({ ...current, [key]: value }));
            }}
          />
        )}
      </Field>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
          {t('checkIn.walkInHeading')}
        </h1>
        <p className="text-base text-muted-foreground">{t('checkIn.walkInLead')}</p>
      </div>
      {failure && <Alert>{failureText(failure, t)}</Alert>}

      {services.length > 1 && (
        <fieldset className="space-y-3" aria-describedby={errors.service ? 'walk-in-service-error' : undefined}>
          <legend className="text-base font-semibold text-heading">{t('checkIn.serviceLegend')}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {services.map((entry) => (
              <label key={entry.id} className={tile(serviceId === entry.id)}>
                <input
                  id={`walk-in-service-${entry.id}`}
                  type="radio"
                  name="walk-in-service"
                  checked={serviceId === entry.id}
                  onChange={() => choose(entry.id)}
                  className={CHECKBOX}
                />
                <span className="text-base font-medium text-heading">{entry.title}</span>
              </label>
            ))}
          </div>
          {errors.service && (
            <p id="walk-in-service-error" className="flex items-center gap-1.5 text-sm font-medium text-danger">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t(errors.service)}
            </p>
          )}
        </fieldset>
      )}

      <section aria-labelledby="walk-in-about" className="space-y-5">
        <h2 id="walk-in-about" className={SECTION_TITLE}>
          <IconTile icon={User} size="sm" />
          {t('details.aboutYou')}
        </h2>
        <div className="grid gap-5 sm:grid-cols-2">
          {field('firstName', 'details.firstName', 'given-name')}
          {field('lastName', 'details.lastName', 'family-name')}
        </div>
        <div className="sm:w-1/2 sm:pr-2.5">{field('birthDate', 'details.dob', 'bday', 'date')}</div>
      </section>

      <section aria-labelledby="walk-in-reach" className="space-y-5 border-t border-border pt-6">
        <div className="space-y-1">
          <h2 id="walk-in-reach" className={SECTION_TITLE}>
            <IconTile icon={Mail} size="sm" />
            {t('details.reachYou')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('waitlist.contactHelp')}</p>
        </div>
        {errors.contact && <Alert>{t(errors.contact)}</Alert>}
        <div className="grid gap-5 sm:grid-cols-2">
          {field('phone', 'details.phone', 'tel', 'tel', false)}
          {field('email', 'details.email', 'email', 'email', false)}
        </div>
        {offerText && <TextWhenCalledBox checked={texts} onChange={setTexts} consent={place.consent} />}
      </section>

      {formsNeeded && !formsReady && (
        <p className="flex items-start gap-3 rounded-xl border border-primary/25 bg-accent p-4 text-base text-heading">
          <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
          {t('checkIn.questionsFirst')}
        </p>
      )}
      {formsNeeded && formsReady && (
        <FormsSummary
          entries={forms.entries}
          pageLang={locale}
          pharmacy={place.brand.name}
          note={t('checkIn.formsGo', { pharmacy: place.brand.name })}
          skippedText={t('checkIn.formSkipped')}
          onChange={openForm}
        />
      )}

      <StepActions>
        <Button variant="outline" onClick={onBack} disabled={busy}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('step.back')}
        </Button>
        {formsReady ? (
          <Button onClick={() => void join()} loading={busy} className="flex-1 sm:flex-none">
            {t('checkIn.join')}
          </Button>
        ) : (
          <Button onClick={() => openForm(Math.max(firstUnsettled(forms), 0))} className="flex-1 sm:flex-none">
            {t('offer.answerQuestions')}
          </Button>
        )}
      </StepActions>
    </div>
  );
}
